// Single-slot IndexedDB cache for the most recently opened learning session.
// Keyed implicitly by (userId, contentId) — any put overwrites the previous
// slot, so the store holds at most one session at a time. Used as a
// stale-while-revalidate layer: LearningPage hydrates from the cache for
// instant render, then refreshes from Firestore in the background.

import type { LearningPath, NodeContent } from '../types/learning-path';

const DB_NAME = 'cogniprint-learning-cache';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';
const SLOT_KEY = 'current';

export interface CachedLearningSession {
  contentId: string;
  userId: string;
  path: LearningPath;
  nodeContents: Record<string, NodeContent>;
  cachedAt: number;
}

function hasIndexedDB(): boolean {
  return typeof indexedDB !== 'undefined';
}

let _dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!hasIndexedDB()) return Promise.reject(new Error('IndexedDB unavailable'));
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
  return _dbPromise;
}

export async function getCachedLearningSession(
  contentId: string,
  userId: string,
): Promise<CachedLearningSession | null> {
  if (!hasIndexedDB()) return null;
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(SLOT_KEY);
      req.onsuccess = () => {
        const session = req.result as CachedLearningSession | undefined;
        if (!session) return resolve(null);
        // Only surface the cache if it belongs to the current user and the
        // content they're opening. Anything else would leak another user's
        // session into this one on a shared device.
        if (session.userId !== userId || session.contentId !== contentId) {
          return resolve(null);
        }
        resolve(session);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function cacheLearningSession(session: {
  contentId: string;
  userId: string;
  path: LearningPath;
  nodeContents: Map<string, NodeContent>;
}): Promise<void> {
  if (!hasIndexedDB()) return;
  try {
    const db = await openDb();
    const payload: CachedLearningSession = {
      contentId: session.contentId,
      userId: session.userId,
      path: session.path,
      nodeContents: Object.fromEntries(session.nodeContents),
      cachedAt: Date.now(),
    };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(payload, SLOT_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch {
    // Non-blocking: a cache failure must never break the learning flow.
  }
}

export async function clearLearningCache(): Promise<void> {
  if (!hasIndexedDB()) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
}
